import React, { useState, useEffect } from "react";
import { CheckCircle2, Circle } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { PageHeader, Loader } from "../components/shared";

const OFFSETS = { d30: 30, d15: 15, d7: 7, d3: 3, d1: 1, d0: 0 };
const LOCALES = { pt: "pt-BR", "pt-PT": "pt-PT", en: "en-US", es: "es-ES", fr: "fr-FR", it: "it-IT" };

function xmasTarget() {
  const now = new Date();
  const y = now.getFullYear();
  const eve = new Date(y, 11, 24);
  return now > eve ? new Date(y + 1, 11, 24) : eve;
}

export default function Planner() {
  const { t, lang } = useI18n();
  const [stages, setStages] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/content/planner?lang=${lang}`),
      api.get("/planning"),
    ]).then(([s, p]) => {
      setStages(s.data.stages);
      setCompleted(p.data.completed || []);
      setOpen(s.data.stages[0]?.id);
    }).catch(() => {});
  }, [lang]);

  if (!stages) return <Loader />;

  const target = xmasTarget();
  const stageDate = (id) => {
    const off = OFFSETS[id];
    if (off == null) return null;
    const d = new Date(target);
    d.setDate(d.getDate() - off);
    return d.toLocaleDateString(LOCALES[lang] || "pt-BR", { day: "numeric", month: "short" });
  };

  const totalTasks = stages.reduce((a, s) => a + s.tasks.length, 0);
  const progress = totalTasks ? Math.round((completed.length / totalTasks) * 100) : 0;

  const toggle = async (taskId) => {
    const done = !completed.includes(taskId);
    setCompleted((p) => (done ? [...p, taskId] : p.filter((x) => x !== taskId)));
    await api.post("/planning/toggle", { task_id: taskId, done });
  };

  return (
    <div>
      <PageHeader title={t("planner.title")} testid="planner-header" />

      <div className="mb-8 flex items-center gap-3">
        <span className="text-sm text-xmas-slate">{t("planner.progress")}</span>
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-xmas-navysurface">
          <div className="h-full rounded-full bg-gradient-to-r from-xmas-pine to-xmas-gold transition-all" style={{ width: `${progress}%` }} />
        </div>
        <span className="font-mono-num text-sm text-xmas-gold">{progress}%</span>
      </div>

      <div className="relative space-y-4 before:absolute before:left-[15px] before:top-2 before:h-full before:w-px before:bg-xmas-gold/20">
        {stages.map((stage) => {
          const doneCount = stage.tasks.filter((task) => completed.includes(task.id)).length;
          const allDone = doneCount === stage.tasks.length;
          const date = stageDate(stage.id);
          return (
            <div key={stage.id} className="relative pl-10" data-testid={`planner-stage-${stage.id}`}>
              <span className={`absolute left-0 top-1 flex h-8 w-8 items-center justify-center rounded-full border-2 ${allDone ? "border-xmas-gold bg-xmas-gold text-xmas-navy" : "border-xmas-gold/40 bg-xmas-navy text-xmas-gold"}`}>
                {allDone ? "✓" : <span className="h-2 w-2 rounded-full bg-xmas-gold" />}
              </span>
              <div className="glass overflow-hidden rounded-2xl">
                <button
                  onClick={() => setOpen(open === stage.id ? null : stage.id)}
                  className="flex w-full items-center justify-between px-5 py-4 text-left"
                >
                  <span className="flex flex-col">
                    <span className="font-display text-lg text-xmas-snow">{stage.label}</span>
                    {date && <span className="font-mono-num text-xs uppercase tracking-wider text-xmas-champagne" data-testid={`planner-date-${stage.id}`}>{date}</span>}
                  </span>
                  <span className="font-mono-num text-xs text-xmas-slate">{doneCount}/{stage.tasks.length}</span>
                </button>
                {open === stage.id && (
                  <div className="space-y-1 px-5 pb-4">
                    {stage.tasks.map((task) => {
                      const isDone = completed.includes(task.id);
                      return (
                        <button
                          key={task.id}
                          onClick={() => toggle(task.id)}
                          data-testid={`planner-task-${task.id}`}
                          className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left text-sm transition hover:bg-white/5"
                        >
                          {isDone ? <CheckCircle2 className="h-5 w-5 text-xmas-gold" /> : <Circle className="h-5 w-5 text-xmas-slate" />}
                          <span className={isDone ? "text-xmas-slate line-through" : "text-xmas-snow"}>{task.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
